import { useAuthForm } from "@/hooks/use-auth-form";
import { useToast } from "@/hooks/use-toast";
import useReferralCode from "@/hooks/useReferralCode";
import { useCaptchaEnabled } from "@/hooks/useCaptchaEnabled";
import { useTracking } from "@/hooks/useInteractionTracking";
import { AuthApiError, AuthError } from "@supabase/supabase-js";
import { useState, useEffect, useRef } from "react";
import { AuthFormActions } from "./AuthFormActions";
import { AuthFormError } from "./AuthFormError";
import { AuthSubmitButton } from "./AuthSubmitButton";
import SignUpFormInputs from "./SignUpFormInputs";
import { supabase } from "@/integrations/supabase/client";

type SignUpFormProps = {
  onSwitchToSignIn: () => void;
  onSuccess?: () => Promise<void>;
  onForgotPassword?: () => void;
};

const SignUpForm = ({
  onSwitchToSignIn,
  onSuccess,
  onForgotPassword,
}: SignUpFormProps) => {

  // use refrerral code from query params if available
  const { referralCode: defaultReferralCode } = useReferralCode(); 
  
  // Check if CAPTCHA is enabled by admin
  const { isCaptchaEnabled, isLoading: isCaptchaSettingLoading } = useCaptchaEnabled();
  
  // but if user modifies the referral code in the form, we should use that instead
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phone: "",
    referralCode: defaultReferralCode || "",
    useReferralCode: Boolean(defaultReferralCode),
    heardAboutSource: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const { handleSignUp } = useAuthForm();
  const { toast } = useToast();
  const { trackFeatureUsage } = useTracking();
  
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip CAPTCHA if disabled by admin or still loading setting
    if (!isCaptchaEnabled || isCaptchaSettingLoading) {
      console.log('⏭️ CAPTCHA is disabled by admin, skipping widget render');
      return;
    }
    
    if (!turnstileRef.current || widgetIdRef.current) return;
    
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    
    if (!siteKey) {
      console.error('❌ VITE_TURNSTILE_SITE_KEY is not defined');
      setError('CAPTCHA configuration error. Please contact support.');
      return;
    }
    
    console.log('🔄 Waiting for Turnstile to load...');
    
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait (50 * 100ms)
    
    const checkTurnstile = setInterval(() => {
      attempts++;
      
      if (window.turnstile) {
        clearInterval(checkTurnstile);
        console.log('✅ Turnstile loaded, rendering widget...');
        
        try {
          widgetIdRef.current = window.turnstile.render(turnstileRef.current!, {
            sitekey: siteKey,
            callback: (token: string) => {
              console.log('✅ CAPTCHA completed');
              setCaptchaToken(token);
            },
          });
          console.log('✅ Widget rendered successfully');
        } catch (err) {
          console.error('❌ Failed to render Turnstile widget:', err);
          setError('CAPTCHA failed to load. Please refresh the page.');
        }
      } else if (attempts >= maxAttempts) {
        clearInterval(checkTurnstile);
        console.error('❌ Turnstile script failed to load after 5 seconds');
        setError('CAPTCHA failed to load. Please refresh the page.');
      }
    }, 100);

    return () => {
      clearInterval(checkTurnstile);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [isCaptchaEnabled, isCaptchaSettingLoading]);

  const getErrorMessage = (error: AuthError) => {
    if (error instanceof AuthApiError) {
      switch (error.status) {
        case 400:
          if (error.message.includes("Invalid login credentials")) {
            return "Invalid email or password. Please check your credentials and try again.";
          }
          if (error.message.includes("Email not confirmed")) {
            return "Please verify your email address before signing in.";
          }
          return "Invalid request. Please check your input and try again.";
        case 422:
          return "Email already registered with an existing account. If you joined a Nimi social last year, please choose 'Sign in' below, then 'Forgot password' to reset your account.";
        case 429:
          return "Too many attempts. Please try again later.";
        case 404:
          return "Account not found. Please check your credentials or sign up.";
        default:
          return error.message;
      }
    }
    const message = error.message || error.stack
    return message || "An unexpected error occurred. Please try again.";
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidForm = () => {
    const { email, password, firstName, lastName } = formData;

    if (!email || !password) {
      setError("Please fill in all required fields");
      return false;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (!firstName) {
      setError("Please enter your first name");
      return false;
    }

    if (!lastName) {
      setError("Please enter your last name");
      return false;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !isValidForm()) return;
    
    // Only require CAPTCHA if enabled by admin
    if (isCaptchaEnabled && !captchaToken) {
      setError("Please complete the CAPTCHA verification");
      return;
    }
    
    setLoading(true);
    setError(null);
    const {
      email,
      password,
      firstName,
      lastName,
      phone,
      useReferralCode,
      heardAboutSource,
    } = formData

    const referralCode = useReferralCode 
      ? formData.referralCode || undefined // don't send empty string
      : undefined;

    // Read partner code synchronously from URL at submit time to avoid
    // nuqs hydration race that can drop the code on remount.
    const submittedPartnerCode =
      new URLSearchParams(window.location.search).get('partnerCode') || undefined;
    
    // Track sign-up attempt
    trackFeatureUsage("auth", "sign_up_attempt", { 
      email, 
      hasReferralCode: !!referralCode,
      hasPartnerCode: !!submittedPartnerCode 
    });
    
    try {
      await handleSignUp({
        email,
        password,
        firstName,
        lastName,
        phone,
        referralCode,
        captchaToken: captchaToken ?? undefined,
        partnerCode: submittedPartnerCode,
        heardAboutSource: heardAboutSource || undefined,
      });

      // Track successful sign-up
      trackFeatureUsage("auth", "sign_up_success", { email });

      // Send welcome email, no need to wait for it
      supabase.functions.invoke('send-welcome-email', {
        body: {
          userId: (await supabase.auth.getUser()).data.user?.id,
          email,
          firstName,
        }
      });

      if (onSuccess) {
        await onSuccess();
      }
    } catch (err) {
      console.error("Authentication error:", err);
      const error = err as AuthError;
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      
      // Track failed sign-up
      trackFeatureUsage("auth", "sign_up_failed", { email, error: errorMessage });
      
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <AuthFormError error={error} />
      <SignUpFormInputs
        formData={formData}
        setFormData={setFormData}
      />
      {/* Only show CAPTCHA widget if enabled */}
      {isCaptchaEnabled && <div ref={turnstileRef} />}
      <div className="space-y-4">
        <AuthSubmitButton loading={loading} isSignUp />
        <AuthFormActions
          isSignUp
          onToggleMode={onSwitchToSignIn}
          onForgotPassword={onForgotPassword}
        />
      </div>
    </form>
  );
};

export default SignUpForm;
